import type {
  FormattingSettingsProps,
  FractionalSecondDigits,
} from "@vuu-ui/vuu-table-types";
import {
  DatePattern,
  DateTimePattern,
  TimePattern,
  dateTimeLabelByType,
  dateTimePattern,
  defaultPatternsByType,
  fallbackDateTimePattern,
  getTemporalInfo,
  getTypeFormattingFromColumn,
  isDatePattern,
  isTimePattern,
  supportedDateTimePatterns,
} from "@vuu-ui/vuu-utils";
import {
  Dropdown,
  FormField,
  FormFieldLabel,
  Option,
  ToggleButton,
  ToggleButtonGroup,
} from "@salt-ds/core";
import React, { SyntheticEvent, useCallback, useMemo, useState } from "react";

const toggleValues = ["date", "time", "both"] as const;

const DEFAULT = "default";
const fractionalSecondDigitOptions = [DEFAULT, "0", "3", "6", "9"] as const;
const timeZoneOptions = [
  DEFAULT,
  "local",
  "UTC",
  "Europe/London",
  "Europe/Paris",
  "America/New_York",
  "Asia/Tokyo",
  "Asia/Hong_Kong",
] as const;
const localeOptions = [
  DEFAULT,
  "en-GB",
  "en-US",
  "de-DE",
  "fr-FR",
  "ja-JP",
] as const;
type ToggleValue = (typeof toggleValues)[number];

function getToggleValue(pattern: DateTimePattern): ToggleValue {
  return !pattern.time ? "date" : !pattern.date ? "time" : "both";
}

type DateTime = keyof DateTimePattern;

const getSelectedPattern = (
  pattern?: DatePattern | TimePattern,
): DatePattern[] | TimePattern[] | undefined => {
  if (isDatePattern(pattern)) {
    return [pattern] as DatePattern[];
  } else if (isTimePattern(pattern)) {
    return [pattern] as TimePattern[];
  } else {
    return undefined;
  }
};

export const DateTimeFormattingSettings: React.FC<FormattingSettingsProps> = ({
  column,
  onChangeFormatting: onChange,
}) => {
  const formatting = getTypeFormattingFromColumn(column);
  const temporalInfo = getTemporalInfo(column);
  // When no pattern has been configured, show the default for the column kind
  const {
    pattern = temporalInfo
      ? dateTimePattern(column.type, temporalInfo.kind)
      : fallbackDateTimePattern,
  } = formatting;
  const toggleValue = useMemo(() => getToggleValue(pattern), [pattern]);

  const [fallbackState, setFallbackState] = useState<Required<DateTimePattern>>(
    {
      time: pattern.time ?? defaultPatternsByType.time,
      date: pattern.date ?? defaultPatternsByType.date,
    },
  );

  const onFractionalSecondDigitsChange = useCallback(
    (_: SyntheticEvent, [value]: string[]) => {
      const { fractionalSecondDigits, ...rest } = formatting;
      void fractionalSecondDigits;
      onChange(
        value === DEFAULT
          ? rest
          : {
              ...rest,
              fractionalSecondDigits: parseInt(
                value,
                10,
              ) as FractionalSecondDigits,
            },
      );
    },
    [formatting, onChange],
  );

  const onFormattingOptionChange = useCallback(
    (key: "locale" | "timeZone") =>
      (_: SyntheticEvent, [value]: string[]) => {
        const { [key]: _previous, ...rest } = formatting;
        void _previous;
        onChange(value === DEFAULT ? rest : { ...rest, [key]: value });
      },
    [formatting, onChange],
  );

  const onPatternChange = useCallback(
    (pattern: DateTimePattern) => onChange({ ...formatting, pattern }),
    [onChange, formatting],
  );

  const onDropdownChange = useCallback<
    <T extends DateTime>(
      dateTime: T,
    ) => (
      e: SyntheticEvent,
      newSelected: Array<Required<DateTimePattern>[T]>,
    ) => void
  >(
    (dateTime) =>
      (_, [selectedPattern]) => {
        const updatedPattern = {
          ...(pattern ?? {}),
          [dateTime]: selectedPattern,
        };
        setFallbackState((s) => ({
          time: updatedPattern.time ?? s.time,
          date: updatedPattern.date ?? s.date,
        }));
        onPatternChange(updatedPattern);
      },
    [onPatternChange, pattern],
  );

  const onToggleChange = useCallback(
    (evnt: SyntheticEvent<HTMLButtonElement, Event>) => {
      const value = evnt.currentTarget.value as ToggleValue;
      switch (value) {
        case "time":
          return onPatternChange({
            [value]: pattern[value] ?? fallbackState[value],
          });
        case "date":
          return onPatternChange({
            [value]: pattern[value] ?? fallbackState[value],
          });
        case "both":
          return onPatternChange({
            time: pattern.time ?? fallbackState.time,
            date: pattern.date ?? fallbackState.date,
          });
      }
    },
    [onPatternChange, pattern, fallbackState],
  );

  return (
    <>
      <FormField labelPlacement="top">
        <FormFieldLabel>{"Display"}</FormFieldLabel>
        <ToggleButtonGroup
          className="vuuToggleButtonGroup"
          onChange={onToggleChange}
          value={toggleValue}
          data-variant="primary"
        >
          {toggleValues.map((v) => (
            <ToggleButton key={v} value={v}>
              {v.toUpperCase()}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </FormField>

      {(["date", "time"] as const)
        .filter((v) => !!pattern[v])
        .map((v) => (
          <FormField labelPlacement="top" key={v}>
            <FormFieldLabel>{`${dateTimeLabelByType[v]} pattern`}</FormFieldLabel>
            <Dropdown<Required<DateTimePattern>[typeof v]>
              onSelectionChange={onDropdownChange(v)}
              selected={getSelectedPattern(pattern[v])}
            >
              {supportedDateTimePatterns[v].map((pattern, i) => (
                <Option key={i} value={pattern}>
                  {pattern}
                </Option>
              ))}
            </Dropdown>
          </FormField>
        ))}

      {pattern.time ? (
        <FormField labelPlacement="top">
          <FormFieldLabel>Fractional second digits</FormFieldLabel>
          <Dropdown<string>
            onSelectionChange={onFractionalSecondDigitsChange}
            selected={[`${formatting.fractionalSecondDigits ?? DEFAULT}`]}
            value={`${formatting.fractionalSecondDigits ?? DEFAULT}`}
          >
            {fractionalSecondDigitOptions
              .filter(
                (digits) =>
                  temporalInfo?.precision === "ns" ||
                  (digits !== "6" && digits !== "9"),
              )
              .map((digits) => (
                <Option key={digits} value={digits}>
                  {digits}
                </Option>
              ))}
          </Dropdown>
        </FormField>
      ) : null}

      <FormField labelPlacement="top">
        <FormFieldLabel>Time zone</FormFieldLabel>
        <Dropdown<string>
          onSelectionChange={onFormattingOptionChange("timeZone")}
          selected={[formatting.timeZone ?? DEFAULT]}
          value={formatting.timeZone ?? DEFAULT}
        >
          {timeZoneOptions.map((timeZone) => (
            <Option key={timeZone} value={timeZone}>
              {timeZone}
            </Option>
          ))}
        </Dropdown>
      </FormField>

      <FormField labelPlacement="top">
        <FormFieldLabel>Locale</FormFieldLabel>
        <Dropdown<string>
          onSelectionChange={onFormattingOptionChange("locale")}
          selected={[formatting.locale ?? DEFAULT]}
          value={formatting.locale ?? DEFAULT}
        >
          {localeOptions.map((locale) => (
            <Option key={locale} value={locale}>
              {locale}
            </Option>
          ))}
        </Dropdown>
      </FormField>
    </>
  );
};
